/**
 * S60b / plan 2 §10.3 — retry button on a failed analysis status message.
 * Fence: ```retry {"run_id":"…"}. POST /analysis/:id/retry (S60a).
 */
import { el } from '../../core/dom.js';
import { api } from '../../core/api.js';
import { resolvedOf } from './shape.js';

/**
 * @param {object} block
 * @param {{ apiPost?: Function }} [actions]
 * @returns {HTMLElement}
 */
export function renderRetry(block, actions) {
  const data = resolvedOf(block);
  const runId = String((data && data.run_id) || '').trim();
  const post = (actions && typeof actions.apiPost === 'function')
    ? actions.apiPost
    : (path, body) => api(path, { method: 'POST', body });

  const status = el('p', { class: 'chat-retry-status', hidden: true });
  const btn = el('button', {
    type: 'button',
    class: 'chat-chip chat-retry-start',
    disabled: !runId,
  }, 'Retry analysis');

  const root = el('div', {
    class: 'chat-block chat-block-retry',
    'data-block-type': 'retry',
    'data-block-id': (block && block.id) || '',
    'data-run-id': runId,
    'aria-label': 'Retry analysis',
  }, btn, status);

  let busy = false;

  btn.addEventListener('click', async () => {
    if (busy || !runId) return;
    busy = true;
    btn.disabled = true;
    btn.textContent = 'Retrying…';
    status.hidden = true;

    const res = await post(`/analysis/${encodeURIComponent(runId)}/retry`);
    if (res && !res.error && res.run && res.run.id) {
      const nextId = String(res.run.id);
      status.hidden = false;
      status.setAttribute('class', 'chat-retry-status');
      status.textContent = `Started new run ${nextId}`;
      btn.textContent = 'Retry analysis';
      return;
    }
    busy = false;
    btn.disabled = false;
    btn.textContent = 'Retry analysis';
    status.hidden = false;
    status.setAttribute('class', 'chat-retry-status is-error');
    const code = res && res.status;
    if (code === 403) {
      status.textContent = 'Not allowed.';
      return;
    }
    if (code === 409) {
      status.textContent = 'Run cannot be retried.';
      return;
    }
    status.textContent = (res && res.error) ? String(res.error) : 'Could not retry.';
  });

  return root;
}
